import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    Input,
    QueryList,
    Signal,
    signal,
    ViewChildren,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { PortfolioFlagsService } from '@ecm/portfolio';

import { CarrouselItemViewModelMapper } from '../../business/mapper/carrousel-item-view-model-mapper';
import { CarrouselItemViewModel } from '../../business/model/carrousel-item.view-model';
import { CarrouselItemMobileComponent } from '../../components/carrousel-item-mobile/carrousel-item-mobile.component';
import { CarrouselItemRedirectLinkComponent } from '../../components/carrousel-item-redirect-link/carrousel-item-redirect-link.component';
import { CarrouselItemComponent } from '../../components/carrousel-item/carrousel-item.component';
import { CarrouselStoreService } from '../../store/carrousel-store.service';
import { SelectedLink } from '../../store/nav-display-config';

@Component({
    selector: 'ecm-carrousel',
    templateUrl: 'carrousel.component.html',
    styleUrls: ['carrousel.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CarrouselItemComponent,
        CarrouselItemRedirectLinkComponent,
        CarrouselItemMobileComponent,
        NgTemplateOutlet,
    ],
})
export class CarrouselComponent {
    @Input() public isXlScreen: boolean;
    @ViewChildren('buttonRef') public buttonRefs: QueryList<ElementRef>;

    private readonly portfolioFlagsService = inject(PortfolioFlagsService);
    private readonly carrouselStore = inject(CarrouselStoreService);
    public isSosh = this.portfolioFlagsService.isSosh;
    public isCustomerBenefits$ = toSignal(this.carrouselStore.checkAncrageCustomerBenefits$);
    public carrouselItems$ = toSignal(this.carrouselStore.items$);

    public isOrangeServices$ = toSignal(this.carrouselStore.checkAncrageOrangeServices$);
    public contracts$ = toSignal(this.carrouselStore.getOfferItems$);

    public isFullAbba = this.portfolioFlagsService.isFullAbba;
    public isBloomActivated = computed(() => this.carrouselStore.isEligibleToBloom());

    public carrouselLinksItems = signal<{ type: string; selected: boolean }[]>([]);

    public carrouselLinksItemsSelected: boolean;

    protected selectedLink: SelectedLink = SelectedLink.EMPTY;
    protected readonly SelectedLink = SelectedLink;

    private readonly carrouselItemViewModelMapper = inject(CarrouselItemViewModelMapper);

    public constructor() {
        const linksSignal = this.carrouselItemViewModelMapper.extractLinks(this.getIsSosh());
        const configSignal = toSignal(this.carrouselStore.getDisplayConfig$, {
            initialValue: { lateralMenu: false, carrousel: false, selectedLink: SelectedLink.EMPTY },
        });

        effect(() => {
            const selectedLink = configSignal().selectedLink;
            const links = linksSignal();

            this.selectedLink = selectedLink;
            this.carrouselLinksItems.set(
                links.map((type) => ({
                    type,
                    selected: type.split('.').pop() === selectedLink,
                })),
            );
            this.carrouselLinksItemsSelected = this.carrouselLinksItems().some((item) => item.selected);
        });
    }

    public select(item: CarrouselItemViewModel) {
        if (item.id) {
            this.carrouselStore.selectItem(item.id, true);
        }

        this.carrouselStore.openLink(item.button);
    }

    public selectContract(index: number, cid: string) {
        const button = this.buttonRefs.toArray()[index].nativeElement;
        button.scrollIntoView({ inline: 'center' });
        if (cid) {
            this.carrouselStore.selectItem(cid);
        }
    }

    public getIsSosh(): Signal<boolean> {
        return this.portfolioFlagsService.isSosh;
    }
}
